// Shared options for job forms (manual entry, edit, and display)

export const employmentTypes = [
  { value: 'vaste_baan', label: 'Vaste baan', googleValue: 'FULL_TIME' },
  { value: 'groei_naar_vast', label: 'Groei naar vast', googleValue: 'FULL_TIME' },
  { value: 'bijbaan', label: 'Bijbaan', googleValue: 'PART_TIME' },
  { value: 'tijdelijk', label: 'Tijdelijk', googleValue: 'TEMPORARY' },
  { value: 'stage', label: 'Stage', googleValue: 'INTERN' },
  { value: 'bbl', label: 'BBL', googleValue: 'INTERN' },
  { value: 'zzp', label: 'ZZP / Freelance', googleValue: 'CONTRACTOR' },
  { value: 'seizoenswerk', label: 'Seizoenswerk', googleValue: 'TEMPORARY' },
] as const;

export const educationLevels = [
  { value: 'geen', label: 'Geen vereiste' },
  { value: 'vmbo', label: 'VMBO' },
  { value: 'mbo', label: 'MBO' },
  { value: 'hbo', label: 'HBO' },
  { value: 'wo', label: 'WO / Universiteit' },
] as const;

export const experienceLevels = [
  { value: 'junior', label: 'Junior (0-2 jaar)' },
  { value: 'medior', label: 'Medior (2-5 jaar)' },
  { value: 'senior', label: 'Senior (5+ jaar)' },
] as const;

export const salaryPeriods = [
  { value: 'uur', label: 'Per uur' },
  { value: 'maand', label: 'Per maand' },
  { value: 'jaar', label: 'Per jaar' },
] as const;

export const drivingLicenses = [
  { value: 'AM', label: 'AM (Brommer)', icon: '🛵' },
  { value: 'A1', label: 'A1 (Motor licht)', icon: '🏍️' },
  { value: 'A2', label: 'A2 (Motor middelzwaar)', icon: '🏍️' },
  { value: 'A', label: 'A (Motor)', icon: '🏍️' },
  { value: 'B', label: 'B (Auto)', icon: '🚗' },
  { value: 'BE', label: 'BE (Auto + aanhanger)', icon: '🚙' },
  { value: 'C', label: 'C (Vrachtwagen)', icon: '🚛' },
  { value: 'C1', label: 'C1 (Vrachtwagen licht)', icon: '🚚' },
  { value: 'CE', label: 'CE (Vrachtwagen + aanhanger)', icon: '🚛' },
  { value: 'D', label: 'D (Bus)', icon: '🚌' },
  { value: 'T', label: 'T (Tractor)', icon: '🚜' },
] as const;

export const popularSkills = [
  'Klantenservice',
  'Communicatie',
  'Teamwork',
  'Microsoft Office',
  'Administratie',
  'Rijbewijs B',
  'Flexibel',
  'Zelfstandig',
] as const;

export const defaultLanguages = [
  { value: 'Nederlands', icon: '🇳🇱' },
  { value: 'Engels', icon: '🇬🇧' },
  { value: 'Duits', icon: '🇩🇪' },
] as const;

export const industries = [
  'Administratie',
  'Bouw & Constructie',
  'Commercie & Sales',
  'Creatief & Design',
  'Detailhandel & Retail',
  'Financiën & Accounting',
  'Gezondheidszorg',
  'Horeca & Catering',
  'ICT & Software',
  'Industrie & Productie',
  'Juridisch',
  'Logistiek & Transport',
  'Marketing & Communicatie',
  'Media & Entertainment',
  'Onderwijs & Training',
  'Overheid',
  'Personeelszaken & HR',
  'Schoonmaak & Facilitair',
  'Techniek',
  'Zakelijke Dienstverlening',
] as const;

export const commonLanguages = [
  'Nederlands',
  'Engels',
  'Duits',
  'Frans',
  'Spaans',
  'Pools',
  'Turks',
  'Arabisch',
] as const;

export const commonBenefits = [
  'Leaseauto',
  'Thuiswerkmogelijkheden',
  'Opleidingsbudget',
  'Pensioenregeling',
  'Reiskostenvergoeding',
  'Laptop/MacBook',
  'Flexibele werktijden',
  'Bonusregeling',
  '13e maand',
  'Sportabonnement',
  'Collectieve zorgverzekering',
  'Vakantiegeld',
  'Gratis parkeren',
  'Kantine/lunch',
] as const;

// Helper to map feed employment types to our standard types
export const mapEmploymentType = (input: string | null | undefined): string => {
  if (!input) return 'vaste_baan';
  
  const normalized = input.toLowerCase().trim();
  
  const mappings: Record<string, string> = {
    'fulltime': 'vaste_baan',
    'full-time': 'vaste_baan',
    'full time': 'vaste_baan',
    'permanent': 'vaste_baan',
    'vast': 'vaste_baan',
    'vaste dienst': 'vaste_baan',
    'vaste baan': 'vaste_baan',
    'loondienst': 'vaste_baan',
    'groei naar vast': 'groei_naar_vast',
    'parttime': 'bijbaan',
    'part-time': 'bijbaan',
    'part time': 'bijbaan',
    'bijbaan': 'bijbaan',
    'zzp': 'zzp',
    'freelance': 'zzp',
    'zelfstandig': 'zzp',
    'contractor': 'zzp',
    'stage': 'stage',
    'intern': 'stage',
    'internship': 'stage',
    'stagiair': 'stage',
    'bbl': 'bbl',
    'tijdelijk': 'tijdelijk',
    'temporary': 'tijdelijk',
    'bepaalde tijd': 'tijdelijk',
    'seasonal': 'seizoenswerk',
    'seizoen': 'seizoenswerk',
    'seizoenswerk': 'seizoenswerk',
    'oproep': 'bijbaan',
    'nul-uren': 'bijbaan',
    'nul uren': 'bijbaan',
    'oproepkracht': 'bijbaan',
  };
  
  return mappings[normalized] || 'vaste_baan';
};

// Helper to map experience levels from feeds
export const mapExperienceLevel = (input: string | null | undefined): string | null => {
  if (!input) return null;
  
  const normalized = input.toLowerCase().trim();
  
  if (normalized.includes('senior') || normalized.includes('5+') || normalized.includes('5 jaar')) {
    return 'senior';
  }
  if (normalized.includes('medior') || normalized.includes('mid') || normalized.includes('2-5')) {
    return 'medior';
  }
  if (normalized.includes('junior') || normalized.includes('starter') || normalized.includes('entry')) {
    return 'junior';
  }
  
  return null;
};

// Helper to parse salary and detect period
export const parseSalaryWithPeriod = (
  salaryStr: string | null | undefined
): { min: number | null; max: number | null; period: string } => {
  if (!salaryStr) return { min: null, max: null, period: 'maand' };
  
  const normalized = salaryStr.toLowerCase();
  
  // Detect period
  let period = 'maand';
  if (normalized.includes('uur') || normalized.includes('hour') || normalized.includes('per uur') || normalized.includes('/u')) {
    period = 'uur';
  } else if (normalized.includes('jaar') || normalized.includes('year') || normalized.includes('jaarlijks')) {
    period = 'jaar';
  }
  
  // Parse Dutch number format helper
  const parseDutchNum = (str: string): number | null => {
    if (!str) return null;
    let cleaned = str;
    if (/\.\d{3}/.test(cleaned)) {
      cleaned = cleaned.replace(/\./g, '');
    }
    cleaned = cleaned.replace(',', '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : Math.round(num * 100) / 100;
  };
  
  // Remove currency symbols and whitespace
  const stripped = salaryStr.replace(/[€$\s]/g, '');
  
  // Find numbers with Dutch decimal format
  const numberMatches = stripped.match(/\d{1,3}(?:\.\d{3})*(?:[,]\d{1,2})?/g);
  
  if (!numberMatches || numberMatches.length === 0) {
    return { min: null, max: null, period };
  }
  
  const min = parseDutchNum(numberMatches[0]);
  const max = numberMatches.length > 1 ? parseDutchNum(numberMatches[1]) : null;
  
  return { min, max, period };
};

// Helper to parse hours
export const parseHours = (
  hoursStr: string | null | undefined
): { min: number | null; max: number | null } => {
  if (!hoursStr) return { min: null, max: null };
  
  const numbers = hoursStr.match(/\d+/g);
  
  if (!numbers || numbers.length === 0) {
    return { min: null, max: null };
  }
  
  const min = parseInt(numbers[0], 10);
  const max = numbers.length > 1 ? parseInt(numbers[1], 10) : null;
  
  return { min: isNaN(min) ? null : min, max: max && !isNaN(max) ? max : null };
};
